import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Flex, Heading, Theme } from '@radix-ui/themes';
import './Header.css';
import { useSiteConfig } from '../../utils/siteConfig';
import ThemeToggle from '../shared/ThemeToggle';

interface NavigationItem {
  path: string;
  label: string;
}

interface SocialLink {
  url: string;
  icon: IconDefinition;
  label: string;
}

const NAVIGATION_ITEMS: NavigationItem[] = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/workwithus', label: 'Contact' },
];

const SocialLinksGroup: React.FC = () => {
  const { config, get } = useSiteConfig();
  // Explicitly-empty key hides the icon; unset falls back to WSQ.
  const instagramUrl =
    'socialInstagramUrl' in config
      ? config.socialInstagramUrl
      : get('socialInstagramUrl', 'https://www.instagram.com/west_slope_quiz_co/');
  const links: SocialLink[] = instagramUrl
    ? [{ url: instagramUrl, icon: faInstagram, label: 'Instagram' }]
    : [];

  return (
    <div className="social-links">
      {links.map(({ url, icon, label }) => (
        <a
          key={label}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="social-link"
          aria-label={label}
        >
          <FontAwesomeIcon icon={icon} />
        </a>
      ))}
    </div>
  );
};

const NavItems: React.FC<{ onNavClick?: () => void }> = ({ onNavClick }) => (
  <ul className="nav-list">
    {NAVIGATION_ITEMS.map(({ path, label }) => (
      <li key={path} className="nav-item">
        <a href={path} onClick={onNavClick}>
          {label}
        </a>
      </li>
    ))}
  </ul>
);

const Header: React.FC = () => {
  const siteConfig = useSiteConfig();
  const brandName = siteConfig.get('brandName', 'West Slope Quiz');
  const brandLogoUrl = siteConfig.get('brandLogoUrl');
  const [isDesktop, setIsDesktop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mediaQuery.matches);

    const handleResize = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
      if (e.matches) {
        setMobileMenuOpen(false);
      }
    };
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <Flex asChild align="center" justify="between" width="100%" className="header">
      <header>
        <Flex asChild align="center" gap={{ initial: '2', md: '4' }} className="header-brand">
          <a href="/">
            {brandLogoUrl && (
              <div className="header-logo">
                <img src={brandLogoUrl} alt={`${brandName} Logo`} />
              </div>
            )}
            <Heading
              as="h1"
              size={{ initial: '5', md: '6' }}
              className="header-title"
            >
              {brandName}
            </Heading>
          </a>
        </Flex>

        <Flex align="center">
        {isDesktop ? (
          <NavigationMenu.Root asChild>
            <nav className="site-nav nav-open">
              <NavigationMenu.List asChild>
                <ul className="nav-list">
                  {NAVIGATION_ITEMS.map(({ path, label }) => (
                    <NavigationMenu.Item key={path} asChild>
                      <li className="nav-item">
                        <NavigationMenu.Link asChild>
                          <a href={path}>{label}</a>
                        </NavigationMenu.Link>
                      </li>
                    </NavigationMenu.Item>
                  ))}
                </ul>
              </NavigationMenu.List>
              <SocialLinksGroup />
              <ThemeToggle compact />
            </nav>
          </NavigationMenu.Root>
        ) : (
          <Dialog.Root
            open={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
          >
            <Dialog.Trigger asChild>
              <button
                className={`burger ${mobileMenuOpen ? 'open' : ''}`}
                aria-label="Toggle navigation"
              >
                <span className="burger-line"></span>
                <span className="burger-line"></span>
                <span className="burger-line"></span>
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              {/* Theme re-applies the brand tokens on the portalled DOM node */}
              <Theme>
                <Dialog.Content asChild>
                  <nav className={`site-nav ${mobileMenuOpen ? 'nav-open' : 'nav-closed'}`}>
                    <VisuallyHidden.Root asChild>
                      <Dialog.Title>Navigation Menu</Dialog.Title>
                    </VisuallyHidden.Root>
                    <NavItems onNavClick={handleMobileNavClick} />
                    <SocialLinksGroup />
                    <ThemeToggle compact />
                  </nav>
                </Dialog.Content>
              </Theme>
            </Dialog.Portal>
          </Dialog.Root>
        )}
        </Flex>
      </header>
    </Flex>
  );
};

export default Header;
